import type { AspectRatioOption } from '../types/index.js';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './language.js';

export * from './language.js';
export * from './api-error-codes.js';
export * from './direct-sale-fonts.js';
export * from './seller-fonts.js';
export * from './phoenix-sku.js';
export * from './integrators.js';

/**
 * Available aspect ratios for designs
 */
export const ASPECT_RATIOS: AspectRatioOption[] = [
  { value: '1:1', label: 'Square', width: 1, height: 1 },
  { value: '3:4', label: 'Portrait', width: 3, height: 4 },
  { value: '4:3', label: 'Landscape', width: 4, height: 3 },
  { value: '16:9', label: 'Wide', width: 16, height: 9 },
  { value: '9:16', label: 'Tall', width: 9, height: 16 },
  { value: '2:3', label: 'Classic Portrait', width: 2, height: 3 },
  { value: '3:2', label: 'Classic Landscape', width: 3, height: 2 },
  { value: '4:5', label: 'Instagram', width: 4, height: 5 },
  { value: '5:4', label: 'Medium', width: 5, height: 4 },
] as const;

/**
 * Default aspect ratio
 */
export const DEFAULT_ASPECT_RATIO = '3:4';

/**
 * Application configuration
 */
/**
 * The one upload ceiling. Web validation, the assets API, the storage
 * service and every multipart route read this, so a file that the upload
 * dialog accepts is a file the server accepts -- before this there were
 * three different numbers and the smallest one won silently.
 */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/** The same number for people, used in error messages and Swagger text. */
export const MAX_UPLOAD_LABEL = '50 MB';

/**
 * Largest value of a Postgres `int4` column (e.g. `shirteeDesignerId`,
 * `externalDesignerId`). Filters beyond it would make Prisma throw instead
 * of matching nothing.
 */
export const POSTGRES_INT4_MAX = 2147483647;

export const APP_CONFIG = {
  API_VERSION: 'v1',
  DEFAULT_LOCALE: DEFAULT_LANGUAGE,
  SUPPORTED_LOCALES: SUPPORTED_LANGUAGES,
  MAX_FILE_SIZE: MAX_UPLOAD_BYTES,
  ALLOWED_IMAGE_TYPES: [
    'image/jpeg',
    'image/png',
    'image/svg+xml',
    'image/webp',
  ] as const,
} as const;

/**
 * Keyboard shortcuts mapping
 */
export const KEYBOARD_SHORTCUTS = {
  'ctrl+z': 'undo',
  'ctrl+shift+z': 'redo',
  'ctrl+y': 'redo',
  'ctrl+s': 'save',
  'ctrl+d': 'duplicate',
  delete: 'deleteSelected',
  backspace: 'deleteSelected',
  escape: 'deselect',
  'ctrl+a': 'selectAll',
  'ctrl+g': 'group',
  'ctrl+shift+g': 'ungroup',
  arrowup: 'nudgeUp',
  arrowdown: 'nudgeDown',
  arrowleft: 'nudgeLeft',
  arrowright: 'nudgeRight',
  'shift+arrowup': 'nudgeFastUp',
  'shift+arrowdown': 'nudgeFastDown',
  'shift+arrowleft': 'nudgeFastLeft',
  'shift+arrowright': 'nudgeFastRight',
} as const;

/**
 * Design status labels (i18n keys)
 */
export const DESIGN_STATUS_LABELS = {
  DRAFT: 'status.draft',
  ACTIVE: 'status.active',
  ARCHIVED: 'status.archived',
} as const;

/**
 * Mockup status labels (i18n keys)
 */
export const MOCKUP_STATUS_LABELS = {
  PENDING: 'mockup.status.pending',
  PROCESSING: 'mockup.status.processing',
  COMPLETED: 'mockup.status.completed',
  FAILED: 'mockup.status.failed',
} as const;

/**
 * Color palette for UI
 */
export const UI_COLORS = {
  primary: {
    50: '#F5F3FF',
    100: '#EDE9FE',
    200: '#DDD6FE',
    300: '#C4B5FD',
    400: '#A78BFA',
    500: '#8B5CF6',
    600: '#7C3AED',
    700: '#6D28D9',
    800: '#5B21B6',
    900: '#4C1D95',
  },
  secondary: {
    500: '#22C55E',
    600: '#16A34A',
  },
  gray: {
    50: '#FAFAFA',
    100: '#F4F4F5',
    200: '#E4E4E7',
    300: '#D4D4D8',
    400: '#A1A1AA',
    500: '#71717A',
    600: '#52525B',
    700: '#3F3F46',
    800: '#27272A',
    900: '#18181B',
  },
} as const;
